import React, { useState, forwardRef } from 'react';

// Enhanced input component with consistent Tailwind styling
export const TextField = forwardRef(({
  label,
  placeholder,
  error,
  helperText,
  variant = 'outlined', // 'outlined', 'standard', 'filled'
  size = 'medium', // 'small', 'medium'
  multiline = false,
  rows = 4,
  fullWidth = false,
  disabled = false,
  required = false,
  type = 'text',
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus = false,
  className = '',
  ...props
}, ref) => {
  const [focused, setFocused] = useState(false);
  
  const handleFocus = (e) => {
    setFocused(true);
    onFocus?.(e);
  };
  
  const handleBlur = (e) => {
    setFocused(false);
    onBlur?.(e);
  };

  const hasValue = value !== undefined && value !== null && value !== '';
  const showLabel = label && (variant === 'outlined' || variant === 'filled');
  const isStandard = variant === 'standard';
  
  // Size classes
  const sizeClasses = {
    small: multiline ? 'text-sm p-2' : 'text-sm px-3 py-2',
    medium: multiline ? 'text-base p-3' : 'text-base px-4 py-3'
  };

  // Base input classes
  const inputBaseClasses = `
    w-full bg-white bg-opacity-5 text-white placeholder-gray-400 
    border transition-all duration-200 ease-in-out
    focus:outline-none resize-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${fullWidth ? 'w-full' : ''}
    ${sizeClasses[size]}
  `;

  // Variant-specific classes
  const variantClasses = {
    outlined: `
      border-gray-300 border-opacity-30 rounded-lg
      focus:border-purple-400 focus:border-opacity-60 focus:ring-2 focus:ring-purple-400 focus:ring-opacity-20
      hover:border-opacity-50
    `,
    standard: `
      border-0 border-b border-gray-300 border-opacity-30 rounded-none
      focus:border-purple-400 focus:border-opacity-80 focus:ring-0
      hover:border-opacity-50 bg-transparent
      px-0 py-2
    `,
    filled: `
      border-gray-300 border-opacity-30 rounded-t-lg rounded-b-none border-b-2
      focus:border-purple-400 focus:border-opacity-80
      hover:border-opacity-50 bg-white bg-opacity-10
    `
  };

  const inputClasses = `${inputBaseClasses} ${variantClasses[variant]} ${className}`;

  const labelClasses = `
    absolute left-3 transition-all duration-200 ease-in-out pointer-events-none
    ${focused || hasValue 
      ? 'text-xs -top-2 bg-white bg-opacity-5 px-1 text-purple-300' 
      : 'text-sm top-3 text-gray-400'
    }
    ${isStandard ? 'left-0 bg-transparent' : ''}
    ${error ? 'text-red-400' : ''}
  `;

  const Component = multiline ? 'textarea' : 'input';

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      {/* Label for outlined/filled variants */}
      {showLabel && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      
      {/* Input/Textarea */}
      <Component
        ref={ref}
        type={multiline ? undefined : type}
        rows={multiline ? rows : undefined}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={isStandard ? label : placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={inputClasses}
        {...props}
      />
      
      {/* Helper text or error */}
      {(helperText || error) && (
        <div className={`mt-1 text-xs ${error ? 'text-red-400' : 'text-gray-400'}`}>
          {error || helperText}
        </div>
      )}
    </div>
  );
});

// Export as Input for backward compatibility
export const Input = ({ ...props }) => {
  return (
    <input
      className="w-full bg-primary border border-textSecondary text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-200"
      {...props}
    />
  );
};